import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BookOpen, Users, CheckCircle2, LogOut, BookMarked, Bell, Award, Menu, X, ChevronDown, Calendar, MessageSquare, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { authFetch, clearAuthToken } from "../utils/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StudentDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [enrolledCoursesOpen, setEnrolledCoursesOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setEnrolledCoursesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsRes, enrollmentsRes, settingsRes, certificatesRes] = await Promise.all([
        authFetch('/dashboard/stats'),
        authFetch('/enrollments/my'),
        authFetch('/settings/school'),
        authFetch('/certificates/my')
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (certificatesRes.ok) setCertificates(await certificatesRes.json());
      
      if (enrollmentsRes.ok) {
        const enrollmentsData = await enrollmentsRes.json();
        setEnrollments(enrollmentsData);
        
        // Load course details for enrolled courses
        const coursePromises = enrollmentsData.map(async (enrollment) => {
          const courseRes = await authFetch(`/courses/${enrollment.course_id}`);
          if (courseRes.ok) {
            return await courseRes.json();
          }
          return null;
        });
        
        const courses = await Promise.all(coursePromises);
        setEnrolledCourses(courses.filter(c => c !== null));
      }
    } catch (error) {
      toast.error('Failed to load dashboard');
    }
  };

  const handleLogout = async () => {
    try {
      await authFetch('/auth/logout', { method: 'POST' });
      clearAuthToken();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      clearAuthToken();
      toast.error('Logout failed');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-[#3A5A40]" strokeWidth={1.5} />
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                {settings?.school_name || 'J423 Community Discipleship Center'}
              </h1>
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-[#3A5A40] hover:bg-[#E9EDC9] rounded-lg"
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-4">
            {/* My Enrolled Courses Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <Button
                variant="ghost"
                onClick={() => setEnrolledCoursesOpen(!enrolledCoursesOpen)}
                data-testid="my-courses-nav-button"
                className="text-[#3A5A40] hover:bg-[#E9EDC9]"
              >
                <GraduationCap className="w-5 h-5 mr-2" strokeWidth={1.5} />
                My Enrolled Courses
                <ChevronDown className={`w-4 h-4 ml-1 transition-transform ${enrolledCoursesOpen ? 'rotate-180' : ''}`} />
              </Button>
              
              {enrolledCoursesOpen && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-[#FEFAE0] border border-[#CCD5AE] rounded-xl shadow-lg z-50 overflow-hidden">
                  <div className="p-3 border-b border-[#CCD5AE]/50 bg-[#E9EDC9]">
                    <p className="text-sm font-medium text-[#344E41]">
                      {enrolledCourses.length} Enrolled Course{enrolledCourses.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  
                  {enrolledCourses.length === 0 ? (
                    <div className="p-4 text-center">
                      <p className="text-sm text-[#588157]">No enrolled courses yet</p>
                      <Button
                        size="sm"
                        onClick={() => { navigate('/courses'); setEnrolledCoursesOpen(false); }}
                        className="mt-2 bg-[#3A5A40] text-white hover:bg-[#344E41]"
                      >
                        Browse Courses
                      </Button>
                    </div>
                  ) : (
                    <div className="max-h-96 overflow-y-auto">
                      {enrolledCourses.map((course) => (
                        <div key={course.course_id} className="border-b border-[#CCD5AE]/30 last:border-b-0">
                          <div className="p-3 hover:bg-[#F5F0DC]">
                            <p className="font-medium text-[#344E41] text-sm mb-2 line-clamp-1">{course.title}</p>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { navigate(`/my-courses/${course.course_id}/activities`); setEnrolledCoursesOpen(false); }}
                                className="flex-1 text-xs border-[#D4A373] text-[#D4A373] hover:bg-[#D4A373] hover:text-white"
                              >
                                <Calendar className="w-3 h-3 mr-1" />
                                Activities
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => { navigate(`/my-courses/${course.course_id}/discussions`); setEnrolledCoursesOpen(false); }}
                                className="flex-1 text-xs border-[#588157] text-[#588157] hover:bg-[#588157] hover:text-white"
                              >
                                <MessageSquare className="w-3 h-3 mr-1" />
                                Discussions
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="p-2 border-t border-[#CCD5AE]/50 bg-[#F5F0DC]">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { navigate('/my-courses'); setEnrolledCoursesOpen(false); }}
                      className="w-full text-[#3A5A40] hover:bg-[#E9EDC9] text-sm"
                    >
                      View All My Courses
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              onClick={() => navigate('/courses')}
              data-testid="courses-nav-button"
              className="text-[#3A5A40] hover:bg-[#E9EDC9]"
            >
              <BookMarked className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Browse Courses
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/notifications')}
              data-testid="notifications-nav-button"
              className="text-[#3A5A40] hover:bg-[#E9EDC9]"
            >
              <Bell className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Notifications
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/certificates')}
              data-testid="certificates-nav-button"
              className="text-[#b4844a] hover:bg-[#E9EDC9]"
            >
              <Award className="w-5 h-5 mr-2" strokeWidth={1.5} />
              Certificates
            </Button>
            <Avatar className="cursor-pointer" onClick={handleLogout}>
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-[#D4A373] text-white">{user?.name?.[0]}</AvatarFallback>
            </Avatar>
            <Button
              variant="ghost"
              onClick={handleLogout}
              data-testid="logout-button"
              className="text-[#588157] hover:bg-[#E9EDC9]"
            >
              <LogOut className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </div>
        </div>

        {/* Mobile navigation drawer */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-[#CCD5AE]/50">
            <div className="flex flex-col gap-2">
              {/* My Enrolled Courses Section */}
              <div className="mb-2">
                <Button
                  variant="ghost"
                  onClick={() => { navigate('/my-courses'); setMobileMenuOpen(false); }}
                  className="justify-start text-[#3A5A40] hover:bg-[#E9EDC9] w-full font-medium"
                >
                  <GraduationCap className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  My Enrolled Courses
                </Button>
                {enrolledCourses.length > 0 && (
                  <div className="ml-7 mt-1 space-y-1">
                    {enrolledCourses.slice(0, 3).map((course) => (
                      <div key={course.course_id} className="pl-2 border-l-2 border-[#CCD5AE]">
                        <p className="text-xs text-[#344E41] font-medium mb-1 line-clamp-1">{course.title}</p>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { navigate(`/my-courses/${course.course_id}/activities`); setMobileMenuOpen(false); }}
                            className="h-6 text-xs text-[#D4A373] hover:bg-[#D4A373]/10 px-2"
                          >
                            <Calendar className="w-3 h-3 mr-1" />
                            Activities
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => { navigate(`/my-courses/${course.course_id}/discussions`); setMobileMenuOpen(false); }}
                            className="h-6 text-xs text-[#588157] hover:bg-[#588157]/10 px-2"
                          >
                            <MessageSquare className="w-3 h-3 mr-1" />
                            Discussions
                          </Button>
                        </div>
                      </div>
                    ))}
                    {enrolledCourses.length > 3 && (
                      <p className="text-xs text-[#A3B18A] pl-2">+{enrolledCourses.length - 3} more courses</p>
                    )}
                  </div>
                )}
              </div>

              <Button
                variant="ghost"
                onClick={() => { navigate('/courses'); setMobileMenuOpen(false); }}
                className="justify-start text-[#3A5A40] hover:bg-[#E9EDC9]"
              >
                <BookMarked className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Browse Courses
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/notifications'); setMobileMenuOpen(false); }}
                className="justify-start text-[#3A5A40] hover:bg-[#E9EDC9]"
              >
                <Bell className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Notifications
              </Button>
              <Button
                variant="ghost"
                onClick={() => { navigate('/certificates'); setMobileMenuOpen(false); }}
                className="justify-start text-[#b4844a] hover:bg-[#E9EDC9]"
              >
                <Award className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Certificates
              </Button>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="justify-start text-[#588157] hover:bg-[#E9EDC9]"
              >
                <LogOut className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Logout
              </Button>
            </div>
          </div>
        )}
      </nav>

      <main className="max-w-7xl mx-auto p-4 sm:p-6 md:p-12">
        <div className="mb-6 sm:mb-8">
          <h2 className="text-2xl sm:text-4xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
            Welcome back, {user?.name}
          </h2>
          <p className="text-base sm:text-lg text-[#588157]">Continue your spiritual journey</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-8 sm:mb-12">
          <Card
            data-testid="stat-enrolled-courses"
            className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#3A5A40]/10 rounded-xl">
                <BookOpen className="w-6 h-6 text-[#3A5A40]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">Enrolled Courses</p>
                <p className="text-3xl font-bold text-[#344E41]">{stats?.enrolled_courses || 0}</p>
              </div>
            </div>
          </Card>

          <Card
            data-testid="stat-completed-courses"
            className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#D4A373]/10 rounded-xl">
                <CheckCircle2 className="w-6 h-6 text-[#D4A373]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">Completed</p>
                <p className="text-3xl font-bold text-[#344E41]">{stats?.completed_courses || 0}</p>
              </div>
            </div>
          </Card>

          <Card
            data-testid="stat-online-users"
            className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#588157]/10 rounded-xl">
                <Users className="w-6 h-6 text-[#588157]" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm text-[#588157]">Online Now</p>
                <p className="text-3xl font-bold text-[#344E41]">{stats?.online_users || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {certificates.length > 0 && (
          <Card className="p-6 bg-gradient-to-br from-[#b4844a]/10 to-[#d4a373]/10 border-2 border-[#b4844a]/30 shadow-lg rounded-2xl mb-12">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Award className="w-8 h-8 text-[#b4844a]" strokeWidth={1.5} />
                <div>
                  <h3 className="text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
                    Your Achievements
                  </h3>
                  <p className="text-sm text-[#588157]">{certificates.length} certificate{certificates.length > 1 ? 's' : ''} earned</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/certificates')}
                className="bg-[#b4844a] text-white hover:bg-[#a07540] shadow-md duration-300 rounded-lg"
                data-testid="view-all-certificates-button"
              >
                View All
              </Button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {certificates.slice(0, 3).map((cert) => (
                <div
                  key={cert.certificate_id}
                  className="p-4 bg-[#FEFAE0] border border-[#b4844a]/20 rounded-xl hover:shadow-md duration-300 cursor-pointer"
                  onClick={() => navigate('/certificates')}
                  data-testid={`cert-badge-${cert.certificate_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#b4844a] to-[#d4a373] rounded-full flex items-center justify-center flex-shrink-0">
                      <Award className="w-6 h-6 text-white" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-[#344E41] text-sm truncate">{cert.module_name}</p>
                      <p className="text-xs text-[#588157]">Certificate Earned</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>My Courses</h3>
            </div>
            {enrollments.length === 0 ? (
              <Card className="p-8 sm:p-12 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
                <div className="w-20 h-20 bg-[#3A5A40]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-10 h-10 text-[#3A5A40]" strokeWidth={1.5} />
                </div>
                <h4 className="text-xl font-bold text-[#344E41] mb-3" style={{ fontFamily: 'Merriweather, serif' }}>
                  Welcome to J423 Community!
                </h4>
                <p className="text-[#588157] mb-2 max-w-md mx-auto">
                  You will see courses and modules here when the admin or teacher admits you.
                </p>
                <p className="text-[#A3B18A] text-sm mb-6">
                  Thank you for signing up! Please wait for your teacher or administrator to enroll you in a course.
                </p>
                <div className="p-4 bg-[#E9EDC9]/50 rounded-xl max-w-sm mx-auto">
                  <p className="text-xs text-[#588157]">
                    <span className="font-semibold">Need help?</span> Contact your teacher or administrator to get started with your learning journey.
                  </p>
                </div>
              </Card>
            ) : (
              <div className="space-y-4">
                {enrollments.map((enrollment) => (
                  <Card
                    key={enrollment.enrollment_id}
                    data-testid={`enrollment-card-${enrollment.course_id}`}
                    className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 shadow-sm hover:shadow-md duration-300 hover:-translate-y-1 rounded-2xl cursor-pointer"
                    onClick={() => navigate(`/courses/${enrollment.course_id}/view`)}
                  >
                    <div className="flex gap-6">
                      {enrollment.course?.thumbnail && (
                        <img
                          src={enrollment.course.thumbnail}
                          alt={enrollment.course?.title}
                          className="w-32 h-32 object-cover rounded-xl"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-[#344E41] mb-2" style={{ fontFamily: 'Merriweather, serif' }}>
                          {enrollment.course?.title}
                        </h4>
                        <p className="text-[#588157] mb-4">{enrollment.course?.description}</p>
                        <div className="flex items-center gap-4">
                          <div className="flex-1 bg-[#E9EDC9] rounded-full h-2">
                            <div
                              className="bg-[#3A5A40] h-2 rounded-full"
                              style={{ width: `${enrollment.progress || 0}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-[#588157]">{enrollment.progress || 0}%</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}